import { inject, Injectable } from '@angular/core';
import { Bot, BotsService } from '@core/bots';
import { TranslateService } from '@ngx-translate/core';
import { ToastService, ToastType } from '@pin-up-global/common';
import { BehaviorSubject, map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { StickersSettingsApiService } from './stickers-settings-api.service';
import {
  BotSticker,
  CreateStickerReq,
  UpdateStickerReq,
} from './stickers-settings.abstraction';

@Injectable()
export class StickersSettingsService {
  #stickersSettingsApiService = inject(StickersSettingsApiService);
  #botsService = inject(BotsService);
  #translateService = inject(TranslateService);
  #toastService = inject(ToastService);

  #update$ = new BehaviorSubject<void>(void 0);

  #botStickersRes$ = this.#update$.pipe(
      switchMap(() => this.#botsService.selectedBotId$),
      switchMap((botId) => this.#stickersSettingsApiService.getBotStickers({ botId })),
      shareReplay(1),
  );

  readonly botStickers$ = this.#botStickersRes$.pipe(map((res) => res.items));

  readonly isLoading$ = this.#stickersSettingsApiService.isLoading$;

  refresh(): void {
    this.#update$.next();
  }

  addSticker(params: Omit<CreateStickerReq, 'botId'>): Observable<BotSticker> {
    return this.#botsService.selectedBotId$.pipe(
        switchMap(botId => this.#stickersSettingsApiService.createSticker({ botId, ...params })),
        tap((sticker) => {
          this.#toastService.show({
            type: ToastType.Success,
            title: this.#translateService.instant('stickersSettingsPage.toast.stickerAdded'),
            message: sticker.name,
          });
          this.refresh();
        }),
    );
  }

  updateSticker(params: UpdateStickerReq): Observable<BotSticker> {
    return this.#stickersSettingsApiService.updateSticker(params).pipe(
        tap((sticker) => {
          this.#toastService.show({
            type: ToastType.Success,
            title: this.#translateService.instant('stickersSettingsPage.toast.stickerUpdated'),
            message: sticker.name,
          });
          this.refresh();
        }),
    );
  }

  removeSticker(id: number): Observable<void> {
    return this.#stickersSettingsApiService.removeSticker({ id }).pipe(
        tap(() => {
          this.#toastService.show({
            type: ToastType.Success,
            title: this.#translateService.instant('stickersSettingsPage.toast.stickerRemoved'),
          });
          this.refresh();
        }),
    );
  }
}
